import React from "react";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart, computeLineSurcharge } from "../context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, surchargeTotal, baseSubtotal } = useCart();

  return (
    <>
      <div
        className={`fixed inset-0 bg-cocoa-dark/40 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-cream z-50 shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-cocoa/10">
          <div>
            <h3 className="font-heading text-2xl text-cocoa-dark">O seu cesto</h3>
            <p className="text-xs uppercase tracking-widest text-rose-accent">
              {items.length} {items.length === 1 ? "item" : "itens"}
            </p>
          </div>
          <button
            data-testid="close-cart-button"
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-cream-alt rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-cocoa-soft" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <ShoppingBag className="w-12 h-12 text-rose mb-4" />
              <p className="font-heading text-2xl text-cocoa-dark">O cesto está vazio</p>
              <p className="text-cocoa-soft text-sm mt-2 max-w-xs">
                Comece a sua encomenda escolhendo dos nossos salgados e brigadeiros artesanais.
              </p>
              <button
                onClick={() => setOpen(false)}
                className="mt-6 bg-rose hover:bg-rose-hover text-cocoa-dark px-6 py-2.5 rounded-full text-sm transition-colors"
              >
                Continuar a comprar
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li
                  key={it.line_key}
                  data-testid={`cart-item-${it.line_key}`}
                  className="flex gap-3 bg-cream-alt/60 rounded-2xl p-3"
                >
                  <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="font-heading text-lg text-cocoa-dark leading-tight">{it.name}</p>
                        <p className="text-[11px] uppercase tracking-wider text-rose-accent mt-0.5">{it.variant_label}</p>
                      </div>
                      <button onClick={() => removeItem(it.line_key)} className="text-cocoa-soft hover:text-rose-accent shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {it.selected_flavors && Object.keys(it.selected_flavors).length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {Object.entries(it.selected_flavors).map(([groupKey, flavors]) => flavors.length > 0 && (
                          <p key={groupKey} className="text-[11px] text-cocoa-soft leading-snug">
                            <span className="capitalize">{groupKey}:</span> {flavors.join(", ")}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center border border-cocoa/15 rounded-full overflow-hidden bg-cream">
                        <button onClick={() => updateQty(it.line_key, it.quantity - 1)} className="px-2 py-1 hover:bg-cream-alt">
                          <Minus className="w-3 h-3 text-cocoa-soft" />
                        </button>
                        <span data-testid={`qty-${it.line_key}`} className="px-3 text-sm text-cocoa-dark">{it.quantity}</span>
                        <button onClick={() => updateQty(it.line_key, it.quantity + 1)} className="px-2 py-1 hover:bg-cream-alt">
                          <Plus className="w-3 h-3 text-cocoa-soft" />
                        </button>
                      </div>
                      <span className="text-sm text-cocoa-dark font-medium">€{(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-cocoa/10 px-6 py-5 bg-cream-deep/60">
            {surchargeTotal > 0 && (
              <div className="flex justify-between text-xs text-cocoa-soft mb-1">
                <span>Subtotal base</span>
                <span>€{baseSubtotal.toFixed(2)}</span>
              </div>
            )}
            {surchargeTotal > 0 && (
              <div className="flex justify-between text-xs text-rose-accent mb-2">
                <span>Acréscimo sabores premium</span>
                <span>+€{surchargeTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline mb-4">
              <span className="text-xs uppercase tracking-widest text-cocoa-soft">Total</span>
              <span data-testid="cart-subtotal" className="font-heading text-3xl text-cocoa-dark">€{subtotal.toFixed(2)}</span>
            </div>
            <Link
              to="/checkout"
              data-testid="checkout-link"
              onClick={() => setOpen(false)}
              className="block text-center bg-cocoa-dark hover:bg-cocoa text-cream py-3.5 rounded-full font-medium transition-colors"
            >
              Finalizar encomenda
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
